<template>
  <div class="absolute right-12 top-16">
    <RevButton
      size="small"
      :to="{
        name: ROUTE_NAMES.LISTINGS.CREATE,
        params: { productId: listing.productId },
      }"
      variant="primary"
    >
      {{ i18n(translations.duplicate) }}
    </RevButton>
  </div>

  <div class="flex items-start justify-between">
    <div>
      <h1 class="heading-1 mb-12">{{ listing.title }}</h1>

      <div>
        {{ i18n(translations.itemId) }} <b>{{ listing.productId }}</b>
      </div>

      <div class="mt-12 flex flex-col md:flex-row md:items-center">
        <div class="body-1">
          <FormattedMessage :definition="translations.grade">
            <template #grade>
              <RevTag
                :label="listing.aestheticGrade.label"
                variant="secondary"
              />
            </template>
          </FormattedMessage>
        </div>

        <div
          class="mt-12 flex flex-col items-center md:ml-16 md:mt-0 md:flex-row"
        >
          <RevInputText
            id="edit-sku"
            v-model="skuValue"
            class="w-full md:w-auto"
            :has-cleaning-icon="false"
            :label="i18n(translations.sku)"
            @keyup.enter="handleUpdateSku"
          />

          <RevButton
            v-if="hasSkuValueChanged"
            class="mt-12 w-full md:ml-12 md:mt-0 md:w-auto"
            :disabled="!skuValue"
            :loading="isUpdatingSku"
            size="small"
            variant="primary"
            @click="handleUpdateSku"
          >
            {{ i18n(translations.save) }}
          </RevButton>
        </div>
      </div>
    </div>

    <RevIllustration
      :alt="listing.title"
      class="hidden md:block"
      :height="120"
      :src="listing.thumbnail?.link?.href"
      :width="120"
    />
  </div>

  <BackboxInfo class="mt-24" :listing />
</template>

<script setup lang="ts">
import { type Error, getApiErrorMessage } from '#imports'
import { computed, ref } from 'vue'

import type { Listing } from '@backmarket/http-api/src/api-specs-seller-backoffice/merchants/listings.types'
import FormattedMessage from '@backmarket/nuxt-module-i18n/FormattedMessage.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'
import { RevIllustration } from '@ds/components/Illustration'
import { RevInputText } from '@ds/components/InputText'
import { RevTag } from '@ds/components/Tag'

import { ROUTE_NAMES } from '~/constants/routes'
import { useListings } from '~/scopes/listings/pages/index/composables/useListings'

import BackboxInfo from '../../../Listing/components/MinifiedInfo/components/BackboxInfo.vue'

import translations from './ListingInfo.translations'

const props = defineProps<{
  listing: Listing
}>()

const { updateListing } = useListings()
const { openErrorToast } = useTheToast()
const i18n = useI18n()
const isUpdatingSku = ref(false)
const skuValue = ref(props.listing?.sku || '')
const hasSkuValueChanged = computed(() => skuValue.value !== props.listing.sku)

async function handleUpdateSku() {
  if (!skuValue.value) return

  isUpdatingSku.value = true

  try {
    await updateListing(props.listing.id, { sku: skuValue.value })
  } catch (error) {
    openErrorToast({
      content: getApiErrorMessage(error as unknown as Error),
    })
  } finally {
    isUpdatingSku.value = false
  }
}
</script>
